import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Newspaper, ArrowRight, CalendarDays } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
};

const CAT_COLOR = {
  Conseils: "bg-blue-50 text-[#2563EB]",
  "Marché": "bg-orange-50 text-[#C85A40]",
  "Actualités": "bg-emerald-50 text-emerald-600",
};

export const LatestArticles = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <section id="actualites" className="scroll-mt-20 bg-slate-50 border-y border-slate-200" data-testid="home-latest-articles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">
              <Newspaper className="w-4 h-4" /> Le blog
            </div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tighter font-extrabold text-slate-900">
              Actualités &amp; Conseils Immo
            </h2>
          </div>
          <Link
            to="/actualites"
            data-testid="home-articles-all"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:gap-3 transition-all"
          >
            Voir tous les articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link
                to={`/actualites/${a.slug}`}
                data-testid={`home-article-${a.slug}`}
                className="group block bg-white border border-slate-200 rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all h-full"
              >
                {a.cover_image ? (
                  <div className="h-44 overflow-hidden">
                    <img src={mediaSrc(a.cover_image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="h-44 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <Newspaper className="w-9 h-9 text-slate-300" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${CAT_COLOR[a.category] || "bg-slate-100 text-slate-600"}`}>
                      {a.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <CalendarDays className="w-3 h-3" /> {fmtDate(a.created_at)}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900 leading-snug group-hover:text-[#2563EB] transition-colors">
                    {a.title}
                  </h3>
                  {a.excerpt && <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
